import React, { type FC, type ImgHTMLAttributes, type HTMLAttributes, SyntheticEvent, useState } from "react";
import makeStyles from "@material-ui/styles/makeStyles";
import classNames from "classnames";

export type TDropShadowProps = HTMLAttributes<HTMLDivElement> & {
    shadowRadius?: string;
    shadowColor?: string;
};

export const DropShadow: FC<TDropShadowProps> = ({
    style = {},
    shadowRadius = "1px",
    shadowColor = "gray",
    children,
    ...rest
}) => {
    return (
        <div
            {...rest}
            style={{
                ...style,
                padding: shadowRadius,
                filter: `drop-shadow(0px 0px ${shadowRadius} ${shadowColor})`,
                display: "flex",
                justifyContent: "center",
            }}
        >
            {children}
        </div>
    );
};

const OBJECT_FIT_FOR_MODE = Object.freeze({
    aspectFill: "cover",
    aspectFit: "contain",
    fill: "fill",
});

export type TResizeMode = keyof typeof OBJECT_FIT_FOR_MODE;

type TSizing = { width: string; height: string };
const SIZING: Readonly<TSizing> = Object.freeze({
    width: "100%",
    height: "100%",
});

const sizingForMode = (mode: TResizeMode, width: number, height: number): TSizing => {
    const isVertical = height > width;

    switch (mode) {
        case "aspectFill":
            return { ...SIZING, ...(isVertical ? { width: "auto" } : { height: "auto" }) };

        case "aspectFit":
            return { ...SIZING, ...(!isVertical ? { width: "auto" } : { height: "auto" }) };

        case "fill":
        default:
            return SIZING;
    }
};

const objectFitForMode = (mode: TResizeMode) => OBJECT_FIT_FOR_MODE[mode] ?? OBJECT_FIT_FOR_MODE.fill;

export type TAspectImageProps = ImgHTMLAttributes<HTMLImageElement> & {
    resizeMode: TResizeMode;
    [dataAttribute: `data-${string}`]: string | null | undefined | number;
};

export const AspectImage: FC<TAspectImageProps> = ({
    onLoad,
    resizeMode,
    style,
    className,
    alt,
    crossOrigin,
    decoding,
    height,
    sizes,
    src,
    srcSet,
    useMap,
    width,
    ...rest
}) => {
    const classes = useStyles();
    const [resizingStyle, setResizingStyle] = useState(SIZING);
    const handleLoad = (event: SyntheticEvent<HTMLImageElement>) => {
        const target = event.currentTarget;

        setResizingStyle(sizingForMode(resizeMode, target.naturalWidth, target.naturalHeight));

        onLoad?.(event);
    };

    if (!src) {
        return null;
    }

    const dataTest = rest["data-test"];

    return (
        <div
            {...rest}
            className={classNames(className, classes.container)}
            data-test={!dataTest ? dataTest : `${dataTest}-container`}
            style={{
                ...style,
                ...resizingStyle,
            }}
        >
            <img
                onLoad={handleLoad}
                data-test={dataTest}
                className={classes.image}
                style={{ objectFit: objectFitForMode(resizeMode) }}
                {...{ alt, crossOrigin, decoding, height, sizes, src, srcSet, useMap, width }}
            />
        </div>
    );
};

const useStyles = makeStyles({
    image: {
        maxWidth: "100%",
        maxHeight: "100%",
        width: "100%",
        height: "100%",
        objectPosition: "center",
    },
    container: {
        maxWidth: "100%",
        maxHeight: "100%",
        display: "flex",
        justifyContent: "center",
    },
});
